/** The bar's click-to-edit popover. Holds a local DRAFT of name + days —
 *  nothing applies until Done; Delete acts immediately. The days field is a
 *  free text buffer so it can be fully backspaced while typing. */
import React from "react";
import { createPortal } from "react-dom";
import type { DepGraphPredType, ExistingWorkCode } from "../../../../models";
import DepChipTypeahead, {
  type DepCandidate,
} from "../../depCore/DepChipTypeahead/DepChipTypeahead";
import WorkCodeField from "../../../shared/WorkCodeField/WorkCodeField";

interface Props {
  name: string;
  durationDays: number;
  /** Current work code ("" = untakted). */
  code: string;
  scheduleId: number;
  paletteCodes: ExistingWorkCode[];
  /** Select this bar + every bar starting on/after it (drag the block). */
  onSelectFromHere?: () => void;
  /** Viewport anchor (the clicked bar's bottom-left corner). */
  anchor: { left: number; top: number };
  onApply: (patch: {
    name?: string;
    durationDays?: number;
    code?: { code: string; name: string; color: string };
  }) => void;
  onDelete: () => void;
  onClose: () => void;
  /** The binding predecessor (recomputed live by the forward pass), if any. */
  drivingPred?: {
    code: string;
    color: string;
    name: string;
    rel: string;
    predId: string;
    type: DepGraphPredType;
    lagDays: number;
  } | null;
  /** In-section candidates for the pred picker (self already filtered out). */
  predCandidates?: DepCandidate[];
  /** Set/replace/retype the predecessor: newPredId null = keep the current
   *  pred, just change type/lag. */
  onSetPred?: (
    newPredId: string | null,
    type: DepGraphPredType,
    lagDays: number,
  ) => void;
  /** Remove the current driving predecessor tie entirely. */
  onRemovePred?: () => void;
}

const BarPopover: React.FC<Props> = ({
  name,
  durationDays,
  code,
  scheduleId,
  paletteCodes,
  onSelectFromHere,
  anchor,
  onApply,
  onDelete,
  onClose,
  drivingPred,
  predCandidates,
  onSetPred,
  onRemovePred,
}) => {
  const [predPickerOpen, setPredPickerOpen] = React.useState(false);
  const predAddRef = React.useRef<HTMLButtonElement | null>(null);
  const [nameStr, setNameStr] = React.useState(name);
  const [daysStr, setDaysStr] = React.useState(String(durationDays));
  const seed = paletteCodes.find((p) => p.code === code);
  const [codeDraft, setCodeDraft] = React.useState({
    code,
    name: seed?.prefix ?? "",
    color: seed?.color ?? "",
  });
  const rootRef = React.useRef<HTMLDivElement | null>(null);
  const closeRef = React.useRef(onClose);
  closeRef.current = onClose;

  // Keep the popover fully on-screen: it opens below the bar, but near the
  // bottom that would clip it, so measure the real size and flip it up / clamp
  // to the viewport (the 220px estimate undershot the ~360px+ tall popover).
  const [pos, setPos] = React.useState({ left: anchor.left, top: anchor.top });
  React.useLayoutEffect(() => {
    const el = rootRef.current;
    if (!el) return;
    const m = 12;
    const h = el.offsetHeight;
    const w = el.offsetWidth;
    setPos({
      left: Math.max(m, Math.min(anchor.left, window.innerWidth - w - m)),
      top: Math.max(m, Math.min(anchor.top, window.innerHeight - h - m)),
    });
  }, [anchor.left, anchor.top]);
  React.useEffect(() => {
    const onDoc = (e: MouseEvent) => {
      const t = e.target as HTMLElement;
      if (rootRef.current?.contains(t)) return;
      // The work-code panel portals to <body> — it's part of this session.
      if (t.closest?.(".work-code-field-pop")) return;
      if (t.closest?.(".dep-typeahead")) return;
      closeRef.current();
    };
    window.addEventListener("mousedown", onDoc, true);
    return () => window.removeEventListener("mousedown", onDoc, true);
  }, []);

  const commit = () => {
    const patch: Parameters<Props["onApply"]>[0] = {};
    const trimmed = nameStr.trim();
    if (trimmed && trimmed !== name) patch.name = trimmed;
    const parsed = parseInt(daysStr, 10);
    if (Number.isFinite(parsed) && parsed >= 1 && parsed !== durationDays) {
      patch.durationDays = parsed;
    }
    if (codeDraft.code.trim() && codeDraft.code !== code) {
      patch.code = codeDraft;
    }
    onApply(patch);
    onClose();
  };

  return createPortal(
    <div
      ref={rootRef}
      className="wbs-board-popover wbs-board-popover--floating"
      style={{ position: "fixed", left: pos.left, top: pos.top }}
      onMouseDown={(e) => e.stopPropagation()}
      onClick={(e) => e.stopPropagation()}
    >
      <button
        type="button"
        className="wbs-board-popover-x"
        aria-label="Close"
        onClick={onClose}
      >
        ✕
      </button>
      <label className="wbs-board-popover-field">
        Task Name
        <input
          type="text"
          className="wbs-board-popover-name"
          style={{ width: `${Math.min(64, Math.max(12, nameStr.length + 1))}ch` }}
          value={nameStr}
          onChange={(e) => setNameStr(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter") commit();
          }}
        />
      </label>
      <label className="wbs-board-popover-field wbs-board-popover-field--days">
        Days
        <input
          type="number"
          min={1}
          value={daysStr}
          onChange={(e) => setDaysStr(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter") commit();
          }}
        />
      </label>
      {onSelectFromHere && (
        <button
          type="button"
          className="wbs-board-popover-fromhere"
          title="Select this bar and everything that starts on or after it — then drag the whole block"
          onClick={() => {
            onSelectFromHere();
            onClose();
          }}
        >
          ⇥ Select from here onward
        </button>
      )}
      <div className="wbs-board-popover-field">
        Work code
        <WorkCodeField
          scheduleId={scheduleId}
          code={codeDraft.code}
          name={codeDraft.name}
          color={codeDraft.color}
          onChange={(p) =>
            setCodeDraft((prev) => ({
              code: p.code ?? prev.code,
              name: p.name ?? prev.name,
              color: p.color ?? prev.color,
            }))
          }
          extraCodes={paletteCodes}
        />
      </div>
      {(drivingPred || onSetPred) && (
        <div className="wbs-board-popover-field wbs-board-popover-pred">
          Pred
          <span className="wbs-board-popover-pred-addwrap">
            {drivingPred ? (
              <button
                type="button"
                ref={predAddRef}
                className="wbs-board-popover-pred-chip"
                title={`Driving predecessor — ${drivingPred.name} · ${drivingPred.rel}. Click to change the tie. Full logic lives on the Ties tab.`}
                onClick={() => onSetPred && setPredPickerOpen((o) => !o)}
              >
                {drivingPred.code && (
                  <b style={{ background: drivingPred.color || "#4a4f57" }}>
                    {drivingPred.code}
                  </b>
                )}
                <span className="wbs-board-popover-pred-name">{drivingPred.name}</span>
                <span className="wbs-board-popover-pred-rel">{drivingPred.rel}</span>
              </button>
            ) : null}
            {drivingPred && onRemovePred && (
              <button
                type="button"
                className="wbs-board-popover-pred-remove"
                title="Remove this predecessor tie"
                onClick={() => {
                  onRemovePred();
                  onClose();
                }}
              >
                ✕
              </button>
            )}
            {!drivingPred && (
              <button
                type="button"
                ref={predAddRef}
                className="wbs-board-popover-pred-add"
                title="Tie a predecessor to this activity"
                onClick={() => setPredPickerOpen((o) => !o)}
              >
                + add
              </button>
            )}
            {predPickerOpen && onSetPred && (
              <DepChipTypeahead
                candidates={predCandidates ?? []}
                anchorRef={predAddRef}
                onClose={() => setPredPickerOpen(false)}
                withRel={{
                  currentId: drivingPred?.predId ?? null,
                  initialType: drivingPred?.type ?? "FS",
                  initialLagDays: drivingPred?.lagDays ?? 0,
                  commitLabel: drivingPred ? "Update" : "Set pred",
                  onCommit: (c, type, lagDays) => {
                    onSetPred(c ? c.instanceId : null, type, lagDays);
                  },
                }}
              />
            )}
          </span>
        </div>
      )}
      <div className="wbs-board-popover-actions">
        <button
          type="button"
          className="wbs-board-popover-btn--delete"
          onClick={onDelete}
        >
          Delete
        </button>
        <button
          type="button"
          className="wbs-board-popover-btn--done"
          onClick={commit}
        >
          Done
        </button>
      </div>
    </div>,
    document.body,
  );
};

export default BarPopover;
